import * as React from "react";
import {useEffect, useState} from "react";
import {initiateOkyc, generateCaptcha, generateOtp, validateOtp} from "./api";
import {TextField, Button, Otp} from "web-com";
import {INPUT_TYPE, StyleType} from "utils/variables";
import {FIELD_NAMES} from "profile/enums";
import {ContainerBox} from "myshubhlife-component/container-box";
import {COLOR} from "utils/variables";
import Loading from "components/loading";
import {regex} from "profile/regex";
import "./styles.scss";
import {IReduxState} from "reducers";
import {connect} from "react-redux";
import {imageBaseUrl} from "config";
import {validate} from "myshubhlife-component/Okyc/validation";

interface IConnectProps {
  dispatch?: (action: any) => void;
  userId: number;
  isAndroid: boolean;
}

interface IOkycProps {
  kycStatus: (status: boolean) => void;
}

const Okyc = (props: IConnectProps & IOkycProps) => {
  const [aadhaar, updateAadhaar] = useState<number>(null);
  const [otp, updateOtp] = useState<string[]>([]);
  const [captcha, updateCaptcha] = useState<string>(null);
  const [isOtp, updateIsOtp] = useState(false);
  const [aadharError, updateAadhaarError] = useState<string>(null);
  const [captchaError, updateCaptchaError] = useState<string>(null);
  const [loading, updateLoading] = useState(false);
  const [okycDetails, updateOkycDetails] = useState<{session_id: string, captcha: string}>(null);
  const [loaderMsg, updateLoaderMsg] = useState("Please wait we are working on it");
  const [msg, updateMsg] = useState<string>(null);

  const initiateOkycSession = async () => {
    updateLoading(true);
    updateLoaderMsg("initiating session please wait...");
    let isResponse = false;
    do {
      try {
        const res = await initiateOkyc();
        updateOkycDetails(res);
        isResponse = true;
        updateLoading(false);
      } catch (err) {
        updateLoading(true);
      }
    } while (!isResponse);
  };

  const fetchCaptcha = async () => {
    updateLoaderMsg("We are fetching new Captcha");
    updateLoading(true);
    updateCaptcha(null);
    try {
      const res = await generateCaptcha({session_id: okycDetails.session_id});
      updateOkycDetails({...okycDetails, captcha: res.captcha});
      updateLoading(false);
    } catch (err) {
      updateLoading(false);
      updateMsg("Please try again");
    }
  };

  const fetchOtp = async () => {
    if (aadhaar && captcha) {
      updateLoaderMsg("We are sending otp to your Aadhaar linked mobile number");
      updateLoading(true);
      try {
        const res = await generateOtp({session_id: okycDetails.session_id, captcha, aadhaar_no: aadhaar});
        if (res.toLowerCase().includes("captcha")) {
          updateCaptchaError(res);
          await initiateOkycSession();
          updateCaptcha("");
          updateMsg(null);
        } else {
          updateMsg(res);
        }
        updateIsOtp(res.toLowerCase().includes("otp"));
        updateLoading(false);
      } catch (err) {
        await initiateOkycSession();
        updateIsOtp(false);
        updateCaptcha("");
        updateOtp([]);
        updateMsg("Please click generate otp again");
        updateLoading(false);
      }
    }
    !aadhaar && updateAadhaarError("Please Enter 12 digit Aadhaar Number");
    !captcha && updateCaptchaError("Please Enter Captcha");
  };

  const confirmOtp = async () => {
    updateLoaderMsg("Confirming Otp");
    updateLoading(true);
    try {
      const res = await validateOtp({
        session_id: okycDetails.session_id,
        otp: Number(otp.join("")),
        userId: props.userId,
      });
      updateLoading(false);
      res.isSuccess ? props.kycStatus(res.isSuccess) : updateMsg(res.message);
    } catch (err) {
      if (err?.response?.data?.message.includes("OTP/TOTP")) {
        updateMsg(err.response?.data?.message);
      } else {
        await initiateOkycSession();
        updateMsg(`${err.response?.data?.message || "something went wrong"}, Please try again!`)
        updateIsOtp(false);
        updateOtp([]);
        updateCaptcha("");
      }
      updateLoading(false);
    }
  };

  return (
    <ContainerBox
      backgroundColor={COLOR.WHITE}
      pageTitle="Aadhaar KYC Verification"
      showBackButton={!props.isAndroid}
    >
      <Loading active={loading} msg={loaderMsg}/>
        <div className="okyc-container">
          <TextField
            type={INPUT_TYPE.NUMBER}
            label="Enter Aadhaar number"
            placeholder="Please enter aadhaar"
            value={aadhaar}
            max={12}
            fieldName={FIELD_NAMES.AADHAAR}
            onChange={(fieldName: string, value: number) => {updateAadhaar(value); updateAadhaarError(null); }}
            handleBlur={(value: number) => String(value).length === 12 ?
              (!new RegExp(regex[FIELD_NAMES.AADHAAR]).test(String(value)) || !validate(value)) &&
              updateAadhaarError("Enter Valid Aadhar Number") :
              updateAadhaarError("Please Enter 12 digit Aadhaar Number")}
            errorMsg={aadharError}
            styleType={StyleType.BORDER_BOTTOM_INPUT}
            disabled={isOtp}
          />
          {aadhaar && new RegExp(regex[FIELD_NAMES.AADHAAR]).test(String(aadhaar)) && !okycDetails &&
          <Button
            buttonText="Initiate session"
            onClick={initiateOkycSession}
            bgColor={COLOR.PRIMARY}
            width={"60%"}
          />}
          {okycDetails &&
          <div className="captcha-container">
            <TextField
              type={INPUT_TYPE.TEXT}
              label="Captcha"
              placeholder="Captcha"
              value={captcha}
              fieldName={FIELD_NAMES.CAPTCHA}
              onChange={(fieldName: string, value: string) => {updateCaptcha(value); updateCaptchaError(null); }}
              handleBlur={(value: string) => !captcha && updateCaptchaError("Please Enter Valid Captcha")}
              errorMsg={captchaError}
              styleType={StyleType.BORDER_BOTTOM_INPUT}
              disabled={isOtp}
            />
            {okycDetails.captcha && <img alt="Captcha" src={`data:image/jpeg;base64,${okycDetails.captcha}`} />}
            {okycDetails.captcha && !isOtp &&
            <img onClick={initiateOkycSession} className="try-again" alt="Try Again" src={`${imageBaseUrl}/img/okyc/try-again.png`} />}
          </div>}
          {isOtp && <Otp
            otpType={INPUT_TYPE.TEXT}
            otp={otp}
            otpLength={6}
            updateOtp={(newotp: string[]) => updateOtp([...newotp])}
          />}
          {msg && <p className="okyc-msg">{msg}</p>}
         {okycDetails &&  <Button
            buttonText={isOtp ? "Validate Otp" : "Generate Otp"}
            onClick={isOtp && otp.join("").length === 6 ? confirmOtp : fetchOtp}
            bgColor={COLOR.PRIMARY}
            width={"60%"}
          />}
        </div>
    </ContainerBox>
  );
};

const mapStateToProps = (state: IReduxState): IConnectProps => {
  return {
    userId: state.login.auth.userId,
    isAndroid: state.login.isAndroid,
  };
};

export default connect<IConnectProps, {}, {}>(mapStateToProps)(Okyc);
